import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SlidersHorizontal, Bookmark, ChevronUp, ChevronDown, X, Save, Trash2, Check } from "lucide-react";

/** Spaltenauswahl + -reihenfolge (persistiert über visibleColumns-Reihenfolge). */
export function ColumnToggleMenu({ columns, visibleColumns, onChange, requiredKeys = [] }) {
  const isVisible = (k) => visibleColumns.includes(k);
  const toggle = (k) => {
    if (requiredKeys.includes(k)) return;
    onChange(isVisible(k) ? visibleColumns.filter((x) => x !== k) : [...visibleColumns, k]);
  };
  const move = (k, dir) => {
    const idx = visibleColumns.indexOf(k);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= visibleColumns.length) return;
    const next = [...visibleColumns];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next);
  };
  // ordered visible first, then hidden
  const ordered = [...visibleColumns.map((k) => columns.find((c) => c.key === k)).filter(Boolean),
    ...columns.filter((c) => !visibleColumns.includes(c.key))];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="column-toggle-btn">
          <SlidersHorizontal className="w-4 h-4" /> Spalten
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Spalten anzeigen / sortieren</p>
        <div className="max-h-72 overflow-y-auto">
          {ordered.map((c) => {
            const vis = isVisible(c.key);
            return (
              <div key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent">
                <Checkbox checked={vis} disabled={requiredKeys.includes(c.key)} onCheckedChange={() => toggle(c.key)} data-testid={`col-toggle-${c.key}`} />
                <span className="flex-1 text-sm truncate">{c.header}</span>
                {vis && (
                  <span className="flex items-center">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(c.key, -1)} aria-label="nach oben"><ChevronUp className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(c.key, 1)} aria-label="nach unten"><ChevronDown className="w-3.5 h-3.5" /></Button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Gespeicherte Ansichten pro Nutzer: anwenden, speichern, löschen. */
export function SavedViewsMenu({ views, onApply, onSave, onDelete, activeViewId }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="saved-views-btn">
          <Bookmark className="w-4 h-4" /> Ansichten{views.length > 0 ? ` (${views.length})` : ""}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Gespeicherte Ansichten</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {views.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">Noch keine Ansichten gespeichert.</div>}
        {views.map((v) => (
          <div key={v.id} className="flex items-center gap-1 px-1">
            <DropdownMenuItem className="flex-1" onSelect={(e) => { e.preventDefault(); onApply(v); }} data-testid={`view-apply-${v.id}`}>
              {activeViewId === v.id && <Check className="w-4 h-4 mr-2 text-primary" />}
              <span className={activeViewId === v.id ? "" : "ml-6"}>{v.name}</span>
            </DropdownMenuItem>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete(v.id)} data-testid={`view-delete-${v.id}`} aria-label="Ansicht löschen"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        <DropdownMenuSeparator />
        {saving ? (
          <div className="p-2 flex items-center gap-2">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Name der Ansicht" className="h-8"
              onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) { onSave(name.trim()); setName(""); setSaving(false); } }} data-testid="view-name-input" />
            <Button size="icon" className="h-8 w-8" disabled={!name.trim()} onClick={() => { onSave(name.trim()); setName(""); setSaving(false); }} data-testid="view-save-confirm"><Save className="w-4 h-4" /></Button>
          </div>
        ) : (
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSaving(true); }} data-testid="view-save-btn">
            <Save className="w-4 h-4 mr-2" /> Aktuelle Ansicht speichern
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Aktive Filter als entfernbare Chips + Zähler + „Alle zurücksetzen". */
export function FilterChips({ chips, onRemove, onClearAll }) {
  if (!chips || chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="filter-chips">
      <span className="text-xs font-medium text-muted-foreground">{chips.length} Filter aktiv:</span>
      {chips.map((chip) => (
        <span key={chip.key} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/20 pl-2.5 pr-1 py-0.5 text-xs font-medium" data-testid={`chip-${chip.key}`}>
          {chip.label}
          <button type="button" onClick={() => onRemove(chip.key)} className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5" aria-label={`Filter ${chip.label} entfernen`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={onClearAll} data-testid="filter-reset-btn">
        Zurücksetzen
      </Button>
    </div>
  );
}
